/**
 * Publishing to the robot.
 *
 * On the control session, not the video one: `usePublisher` from the SDK binds to the session
 * that is carrying the camera downlink, where a large frame in flight delays a stick movement
 * behind it. The SDK opens a second socket for exactly this and it is what teleoperation
 * should use.
 */
import { CongestionControl, Priority, Reliability, useCtrlSession } from "adamo-react";
import { useEffect, useMemo, useRef } from "react";

type Publisher = { put: (bytes: Uint8Array) => Promise<void> };

const REALTIME = { priority: Priority.REAL_TIME, congestionControl: CongestionControl.DROP,
                   reliability: Reliability.BEST_EFFORT, express: true };
const COMMAND = { priority: Priority.INTERACTIVE_HIGH, congestionControl: CongestionControl.BLOCK,
                  reliability: Reliability.RELIABLE, express: true };

/**
 * A publisher that is safe to call before it exists.
 *
 * Controller frames start the moment the headset does; waiting for the socket by dropping
 * them is right (a stick position a second old is not worth sending), waiting by throwing in
 * the render loop is not.
 */
function usePublisherOn(keyexpr: string | null, options: object): { send: (bytes: Uint8Array) => void } {
  const session = useCtrlSession();
  const publisher = useRef<Publisher | null>(null);

  useEffect(() => {
    if (!session || !keyexpr) return;
    let live = true;
    let handle: (Publisher & { undeclare?: () => Promise<void> }) | null = null;
    void (async () => {
      try {
        const declared = await session.declarePublisher(keyexpr, options);
        if (!live) { void declared.undeclare?.(); return; }
        handle = declared as Publisher & { undeclare?: () => Promise<void> };
        publisher.current = handle;
      } catch (err) {
        console.warn("could not declare", keyexpr, err);
      }
    })();
    return () => {
      live = false;
      publisher.current = null;
      void handle?.undeclare?.().catch(() => undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, keyexpr]);

  return useMemo(() => ({
    send(bytes: Uint8Array) {
      const p = publisher.current;
      if (!p) return;
      p.put(bytes).catch((err) => {
        const message = err instanceof Error ? err.message : String(err);
        if (/closed|not connected/i.test(message)) window.dispatchEvent(new CustomEvent("adamo-link-down", { detail: message }));
      });
    },
  }), []);
}

export const useHandPublisher = (topic: string | null) => usePublisherOn(topic, REALTIME);
export const useActionPublisher = (topic: string | null) => usePublisherOn(topic, COMMAND);
